import { useEffect, useState, useCallback } from "react";
import { apiTech } from "@/lib/api";
import { toast } from "sonner";
import {
  ListChecks, Calendar, BarChart3, User as UserIcon,
  PhoneCall, PackageOpen,
} from "lucide-react";
import TechToday from "@/pages/portals/tech/TechToday";
import TechSchedule from "@/pages/portals/tech/TechSchedule";
import TechHistory from "@/pages/portals/tech/TechHistory";
import TechProfile from "@/pages/portals/tech/TechProfile";
import TechJobDetail from "@/pages/portals/tech/TechJobDetail";
import TechParts from "@/pages/portals/tech/TechParts";

const DISPATCH_TEL = "+12108001710";
const TABS = [
  { key: "open", label: "Open", icon: ListChecks },
  { key: "parts", label: "Parts", icon: PackageOpen },
  { key: "schedule", label: "Schedule", icon: Calendar },
  { key: "history", label: "History", icon: BarChart3 },
  { key: "profile", label: "Profile", icon: UserIcon },
];

export default function TechPortal({ info, onLogout }) {
  const [tab, setTab] = useState("open");
  const [openJobId, setOpenJobId] = useState(null);
  const [me, setMe] = useState(null);

  const reloadMe = useCallback(() => {
    apiTech.get("/tech/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);
  useEffect(() => { reloadMe(); }, [reloadMe]);

  async function toggleDuty() {
    try {
      const r = await apiTech.post("/tech/me/toggle-duty");
      setMe((m) => ({ ...m, on_duty: r.data.on_duty }));
      toast.success(r.data.on_duty ? "You're now on duty" : "You're off duty");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  }

  return (
    <div className="min-h-screen bg-[#002060] text-[#FFFFFF] flex flex-col">
      {/* Header */}
      <header className="bg-[#002060] border-b border-[#FFFFFF]/15 sticky top-0 z-30">
        <div className="max-w-md mx-auto px-4 h-14 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#FFFFFF]/70 font-medium">
              {me?.on_duty ? "On duty" : "Off duty"}
            </div>
            <div className="font-semibold truncate text-[#FFFFFF]">
              Hi, {info?.name?.split(" ")[0] || me?.name?.split(" ")[0] || "Tech"}
            </div>
          </div>
          <button
            type="button"
            onClick={toggleDuty}
            data-testid="tech-toggle-duty"
            className={`shrink-0 inline-flex items-center gap-2 h-9 px-3 rounded-full text-xs font-semibold transition border ${
              me?.on_duty
                ? "bg-[#FF6600] border-[#FF6600] text-[#FFFFFF] hover:bg-[#FF6600]/90"
                : "bg-transparent border-[#FFFFFF]/30 text-[#FFFFFF]/70 hover:bg-[#FFFFFF]/10"
            }`}
            aria-label="Toggle duty"
          >
            <span className={`w-1.5 h-1.5 rounded-full ${me?.on_duty ? "bg-[#FFFFFF]" : "bg-[#FFFFFF]/40"}`} />
            {me?.on_duty ? "On" : "Off"}
          </button>
        </div>
      </header>

      {/* Tab content */}
      <main className="max-w-md mx-auto w-full flex-1 p-4 pb-28 space-y-3">
        {tab === "open" && <TechToday onOpenJob={setOpenJobId} />}
        {tab === "parts" && <TechParts onOpenJob={setOpenJobId} />}
        {tab === "schedule" && <TechSchedule onOpenJob={setOpenJobId} />}
        {tab === "history" && <TechHistory onOpenJob={setOpenJobId} />}
        {tab === "profile" && <TechProfile me={me} onLogout={onLogout} onToggleDuty={toggleDuty} />}
      </main>

      {/* Floating call-dispatch button */}
      <a
        href={`tel:${DISPATCH_TEL}`}
        data-testid="tech-call-dispatch"
        aria-label="Call dispatch"
        className="fixed bottom-24 right-5 z-30 bg-[#FF6600] hover:bg-[#FF6600] active:scale-95 text-[#FFFFFF] rounded-full h-14 w-14 grid place-items-center shadow-xl transition"
      >
        <PhoneCall className="w-5 h-5" />
      </a>

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 inset-x-0 z-40 bg-[#002060] border-t border-[#FFFFFF]/15 pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-md mx-auto grid grid-cols-5">
          {TABS.map((t) => {
            const active = tab === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tech-tab-${t.key}`}
                className={`flex flex-col items-center justify-center py-2.5 transition-colors ${
                  active ? "text-[#FF6600]" : "text-[#FFFFFF]/70 hover:text-[#FFFFFF]"
                }`}
                aria-current={active ? "page" : undefined}
                aria-label={t.label}
              >
                <Icon className={`w-5 h-5 ${active ? "stroke-[2.2]" : ""}`} />
                <span className={`text-[11px] mt-0.5 ${active ? "font-semibold" : ""}`}>{t.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Rich job detail drawer */}
      {openJobId && (
        <TechJobDetail
          jobId={openJobId}
          onClose={() => setOpenJobId(null)}
        />
      )}
    </div>
  );
}
